// Whether a customer is billed by invoice rather than by card. Two independent
// publicData flags mean the same thing here: `invoiceEnabled` (set by the
// request-invoicing endpoint or by ops in Console) and `proCustomer` (written
// outside this repository). This file is CommonJS because the server
// (server/api/accept-offer-invoice.js) requires it as the acceptance gate; it
// must not import any ESM module.

// The transition name is repeated here instead of imported from
// ./transaction (ESM). src/util/invoicing.test.js asserts the two agree.
const TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE = 'transition/accept-custom-offer-invoice';

// Payment term shown to every invoice customer, in days.
const INVOICE_PAYMENT_TERM_DAYS = 14;

// Countries whose companies are looked up in Clevenio (business-id lookup and
// automatic decision). Our Clevenio plan licenses the Finnish company database
// only — Swedish, Norwegian and Danish searches answer 403 — so every other
// country goes through the manual form and ops review. Shared so the request
// form and the server cannot drift apart.
const CLEVENIO_COUNTRIES = ['FI'];

const INVOICE_OPTION = {
  AVAILABLE: 'available', // approved, and this transaction has the invoice transition
  REQUEST: 'request', // not approved, nothing pending: offer the request flow
  PENDING: 'pending', // request under ops review
  DENIED: 'denied', // request denied: contact support
  UNSUPPORTED: 'unsupported', // approved, but the transaction predates the process push
};

// Fully optional-chained so a `null` user (the first client render on an
// auth-gated route) returns `false`.
const isInvoiceBilledCustomer = user =>
  user?.attributes?.profile?.publicData?.invoiceEnabled === true ||
  user?.attributes?.profile?.publicData?.proCustomer === true;

// `nextTransitions` is TransactionPage.processTransitions: the result of
// sdk.processTransitions.query for this transaction.
const invoiceOptionState = (currentUser, nextTransitions) => {
  const supported = (nextTransitions || []).some(
    t => t?.attributes?.name === TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE
  );
  if (isInvoiceBilledCustomer(currentUser)) {
    return supported ? INVOICE_OPTION.AVAILABLE : INVOICE_OPTION.UNSUPPORTED;
  }
  const status = currentUser?.attributes?.profile?.privateData?.invoicing?.status;
  if (status === 'pending') return INVOICE_OPTION.PENDING;
  if (status === 'denied') return INVOICE_OPTION.DENIED;
  return INVOICE_OPTION.REQUEST;
};

// The account page's invoice block (spec §1 state table). Approval wins over
// any stored request status, a request outcome wins over the profile, a
// BUSINESS profile may request, and everyone else is told invoicing is for
// companies. Whether the profile is complete is the page's gate, not this
// helper's.
const INVOICING_ACCOUNT = {
  APPROVED: 'approved',
  PENDING: 'pending',
  DENIED: 'denied',
  ELIGIBLE: 'eligible',
  NOT_BUSINESS: 'notBusiness',
};

const invoicingAccountState = user => {
  if (isInvoiceBilledCustomer(user)) return INVOICING_ACCOUNT.APPROVED;
  const privateData = user?.attributes?.profile?.privateData || {};
  const status = privateData.invoicing?.status;
  if (status === 'pending') return INVOICING_ACCOUNT.PENDING;
  if (status === 'denied') return INVOICING_ACCOUNT.DENIED;
  return privateData.billing?.customerType === 'BUSINESS'
    ? INVOICING_ACCOUNT.ELIGIBLE
    : INVOICING_ACCOUNT.NOT_BUSINESS;
};

// The identity an approved invoice customer is billed as stays locked on
// every billing form while a request is on file; the address stays editable
// (billing page decision 8). Shared by the BillingPage, the offer modal and
// the checkout.
const billingLockedFields = user =>
  isInvoiceBilledCustomer(user) && user?.attributes?.profile?.privateData?.invoicing
    ? ['customerType', 'legalName']
    : [];

module.exports = {
  CLEVENIO_COUNTRIES,
  isInvoiceBilledCustomer,
  invoiceOptionState,
  invoicingAccountState,
  billingLockedFields,
  INVOICE_OPTION,
  INVOICING_ACCOUNT,
  INVOICE_PAYMENT_TERM_DAYS,
  TRANSITION_ACCEPT_CUSTOM_OFFER_INVOICE,
};
